import { useState, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

const ReservationPage = () => {
  const { t } = useTranslation();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    const form = e.currentTarget;
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
    try {
      await fetch('https://readdy.ai/api/form/d7b6bmk9quqsda8uett0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-amber-900">
      {/* 상단 헤더 */}
      <div className="relative h-48 flex items-center justify-center overflow-hidden">
        <img
          src="https://public.readdy.ai/ai/img_res/94d5b35441a66d50cbbcc056a015bd21.jpg"
          alt="예약 배경"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative z-10 text-center">
          <Link to="/" className="inline-block mb-4">
            <img
              src="https://storage.readdy-site.link/project_files/f1a4733f-f142-44b4-9a4b-319ee3c3a442/cf5d4cea-d623-4733-86a8-803e415ede74_20161028_-yeong-bin-ho--ro-go_ol1574127236.png?v=7dd96618540c35f269119a9b3765e2fd"
              alt="YOUNGBIN HOTEL"
              className="h-12 object-contain brightness-0 invert"
            />
          </Link>
          <p className="text-amber-300 text-xs tracking-[0.3em] uppercase">{t('res_tag')}</p>
          <h1 className="text-2xl md:text-3xl font-bold text-white mt-1" style={{ fontFamily: "'Playfair Display', serif" }}>
            {t('res_title')}
          </h1>
        </div>
      </div>

      {/* 예약 폼 */}
      <div className="max-w-3xl mx-auto px-6 py-16">
        {submitted ? (
          <div className="text-center py-16">
            <div className="w-20 h-20 flex items-center justify-center mx-auto mb-6 bg-amber-600 rounded-full">
              <i className="ri-check-line text-white text-3xl" />
            </div>
            <p className="text-white text-xl font-semibold mb-3">{t('res_success_title')}</p>
            <p className="text-amber-200/70 text-sm mb-8">{t('res_success_desc')}</p>
            <Link
              to="/"
              className="inline-block px-8 py-3 bg-amber-600 hover:bg-amber-500 text-white rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
            >
              홈으로 돌아가기
            </Link>
          </div>
        ) : (
          <form
            id="reservation-page-form"
            data-readdy-form
            onSubmit={handleSubmit}
            className="bg-white/10 backdrop-blur-sm rounded-lg p-8 flex flex-col gap-5"
          >
            <input type="hidden" name="recipient_email" value="youngbinhotel@hotmail.com" />
            <input type="hidden" name="form_title" value="영빈호텔 실시간 예약 문의" />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-amber-200 text-xs mb-1.5">{t('res_name')} *</label>
                <input
                  name="name"
                  required
                  placeholder={t('res_name_ph')}
                  className="w-full bg-white/10 border border-white/20 rounded-md px-4 py-2.5 text-white placeholder-white/40 text-sm focus:outline-none focus:border-amber-300"
                />
              </div>
              <div>
                <label className="block text-amber-200 text-xs mb-1.5">{t('res_phone')} *</label>
                <input
                  name="phone"
                  required
                  placeholder={t('res_phone_ph')}
                  className="w-full bg-white/10 border border-white/20 rounded-md px-4 py-2.5 text-white placeholder-white/40 text-sm focus:outline-none focus:border-amber-300"
                />
              </div>
            </div>

            <div>
              <label className="block text-amber-200 text-xs mb-1.5">{t('res_email')}</label>
              <input
                name="email"
                type="email"
                placeholder="example@email.com"
                className="w-full bg-white/10 border border-white/20 rounded-md px-4 py-2.5 text-white placeholder-white/40 text-sm focus:outline-none focus:border-amber-300"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-amber-200 text-xs mb-1.5">{t('res_checkin')} *</label>
                <input
                  name="checkin"
                  type="date"
                  required
                  className="w-full bg-white/10 border border-white/20 rounded-md px-4 py-2.5 text-white text-sm focus:outline-none focus:border-amber-300"
                />
              </div>
              <div>
                <label className="block text-amber-200 text-xs mb-1.5">{t('res_checkout')} *</label>
                <input
                  name="checkout"
                  type="date"
                  required
                  className="w-full bg-white/10 border border-white/20 rounded-md px-4 py-2.5 text-white text-sm focus:outline-none focus:border-amber-300"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-amber-200 text-xs mb-1.5">{t('res_room')}</label>
                <select
                  name="room_type"
                  className="w-full bg-white/10 border border-white/20 rounded-md px-4 py-2.5 text-white text-sm focus:outline-none focus:border-amber-300"
                >
                  <option value="Single Room" className="text-gray-800">{t('res_room1')}</option>
                  <option value="Twin Room" className="text-gray-800">{t('res_room2')}</option>
                  <option value="Double Room" className="text-gray-800">{t('res_room3')}</option>
                  <option value="Triple Room" className="text-gray-800">{t('res_room4')}</option>
                  <option value="Ondol Room" className="text-gray-800">{t('res_room5')}</option>
                  <option value="Suite Room" className="text-gray-800">{t('res_room6')}</option>
                </select>
              </div>
              <div>
                <label className="block text-amber-200 text-xs mb-1.5">인원 수 *</label>
                <select
                  name="guests"
                  required
                  className="w-full bg-white/10 border border-white/20 rounded-md px-4 py-2.5 text-white text-sm focus:outline-none focus:border-amber-300"
                >
                  <option value="1명" className="text-gray-800">1명</option>
                  <option value="2명" className="text-gray-800">2명</option>
                  <option value="3명" className="text-gray-800">3명</option>
                  <option value="4명 이상" className="text-gray-800">4명 이상</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-amber-200 text-xs mb-1.5">{t('res_message')}</label>
              <textarea
                name="message"
                rows={4}
                maxLength={500}
                placeholder={t('res_message_ph')}
                className="w-full bg-white/10 border border-white/20 rounded-md px-4 py-2.5 text-white placeholder-white/40 text-sm focus:outline-none focus:border-amber-300 resize-none"
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full py-3.5 bg-amber-500 hover:bg-amber-400 text-white font-semibold rounded-md text-sm transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60"
            >
              {submitting ? t('res_submitting') : t('res_submit')}
            </button>

            <p className="text-center text-amber-200/50 text-xs">
              문의사항은 <a href="tel:+82-2-2277-1141" className="underline hover:text-amber-300 cursor-pointer">02-2277-1141</a> 로 연락주세요
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

export default ReservationPage;
